import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import financialService from '../../services/financialService';
import './FinancialDashboard.css';

const ComparativeCharts = ({ marketplace, cacheKey, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones' }) => {
  const [loading, setLoading] = useState(true);
  const [chartsData, setChartsData] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    loadCharts();
  }, [marketplace, cacheKey, horizonteTemporal, tipoVolumen]);
  
  const loadCharts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await financialService.getComparativeCharts(
        marketplace, 
        cacheKey,
        horizonteTemporal,
        tipoVolumen
      );
      
      if (result.success) {
        setChartsData(result);
      }
    } catch (err) {
      console.error('Error cargando gráficos comparativos:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando gráficos comparativos...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <p>❌ {error}</p>
        <button onClick={loadCharts} className="btn btn-primary">
          🔄 Reintentar
        </button>
      </div>
    );
  }
  
  if (!chartsData) {
    return <div className="no-data">No hay datos disponibles</div>;
  }
  
  const getRangoBadgeClass = (rango) => {
    const rangoLower = rango.toLowerCase().replace(' ', '-');
    return `badge badge-${rangoLower}`;
  };
  
  return (
    <div className="comparative-charts">
      <div className="chart-section">
        <h3>📊 Comparativo: Precio vs Utilidad vs Costo</h3>
        <p className="chart-description">
          Cada punto representa una publicación. El color indica el costo total.
          Use el hover para ver detalles de cada producto.
        </p>
        
        <div className="chart-container">
          <Plot
            data={chartsData.scatter_chart.data}
            layout={{
              ...chartsData.scatter_chart.layout,
              autosize: true
            }}
            config={{
              responsive: true,
              displayModeBar: true,
              modeBarButtonsToRemove: ['pan2d', 'lasso2d'],
              displaylogo: false
            }}
            style={{ width: '100%', height: '600px' }}
            useResizeHandler={true}
          />
        </div>
        
        <div className="chart-insights">
          <p>💡 <strong>Insights:</strong></p>
          <ul>
            <li>Puntos en la parte superior derecha: Alta utilidad y alto precio (productos premium)</li>
            <li>Puntos en la parte inferior: Productos con pérdida o baja rentabilidad</li>
            <li>Color más intenso (amarillo/verde): Mayor costo del producto</li>
          </ul>
        </div>
      </div>
      
      <div className="margin-analysis-section">
        <h3>💰 Margen de Utilidad por Rango de Precio</h3>
        <p className="chart-description">
          Análisis de rentabilidad según el rango de precio del producto.
        </p>
        
        <div className="margin-table-container">
          <table className="margin-table">
            <thead>
              <tr>
                <th>Rango de Precio</th>
                <th>Utilidad Promedio</th>
                <th>Precio Promedio</th>
                <th>Cantidad de Productos</th>
                <th>Margen %</th>
              </tr>
            </thead>
            <tbody>
              {chartsData.margin_by_range.map((row, idx) => (
                <tr key={idx} className={row['Margen %'] < 0 ? 'negative-margin' : ''}>
                  <td>
                    <span className={getRangoBadgeClass(row.precio_rango)}>
                      {row.precio_rango}
                    </span>
                  </td>
                  <td>${row['Utilidad Promedio']?.toFixed(2)}</td>
                  <td>${row['Precio Promedio']?.toFixed(2)}</td>
                  <td>{row.Cantidad}</td>
                  <td className={row['Margen %'] < 0 ? 'negative' : 'positive'}>
                    {row['Margen %']?.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="margin-insights">
          <p>💡 <strong>Interpretación:</strong></p>
          <ul>
            <li><strong>Margen positivo alto:</strong> Rango de precio óptimo para rentabilidad</li>
            <li><strong>Margen bajo o negativo:</strong> Revisar estrategia de precios o costos</li>
            <li><strong>Cantidad alta:</strong> Concentración de productos en ese rango</li>
          </ul>
        </div>
      </div>
      
      <div className="summary-stats">
        <div className="stat-card">
          <div className="stat-label">Total de Puntos Analizados</div>
          <div className="stat-value">{chartsData.total_points?.toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
};

export default ComparativeCharts;

